<template>
  <div>
    <va-modal
      class="evollu-form"
      :value="showModal"
      id="eventFormModal"
      ref="eventFormModal"
      size="large"
      :noOutsideDismiss="true"
      :okText="showDatePickers ? $t('modals.buttons.save') : $t('buttons.next')"
      :cancelText="$t('users.cancel')"
      :dangetText="$t('users.delete')"
      :showDelete="event !== null && event !== undefined"
      @ok="submit"
      :okDisabled="isSubmitting || !canSubmitForm"
      @cancel="cancel"
      @remove="remove">

      <div class="action-icons" v-if="lead && isUpdating">
        <va-popover
          placement="left"
          :message="$t('leads-web.details-modal.title')">
          <va-button large icon="fa fa-info" color="gray" @click.prevent="handleShowLeadDetail"></va-button>
        </va-popover>
      </div>
      <div slot="header" class="modal-title">
        {{ getModalTitle() }}
      </div>

      <div class="row custom-outlined" v-if="showDatePickers">
        <div class="col-12">
          <h6>
            {{ $t('leads.agenda.confirm_date') }}
          </h6>
        </div>


        <div class="row datePickers">
          <div class="col-12 col-md-6">
            <label class="form-label">{{ $t('leads.agenda.start') }}</label>
            <DatePicker
              id="date-picker-appointment-start"
              v-model="form.start"
              :config=datePickerConfig
              :clearable="false"
              iconClass="i-clock"
              @input="eventFormErrors.has('start') ? eventFormErrors.clear('start') : null"
            >
              <span slot="icon">
                <i class="i-clock"></i>
              </span>
            </DatePicker>
            <small v-show="eventFormErrors.has('start')" class="help text-danger">
              {{ eventFormErrors.get('start') | translate }}
            </small>
          </div>

          <div class="col-12 col-md-6">
            <label class="form-label">{{ $t('leads.agenda.end') }}</label>
            <DatePicker
              id="date-picker-appointment-end"
              v-model="form.end"
              :config=datePickerConfig
              :clearable="false"
              iconClass="i-clock"
              @input="eventFormErrors.has('end') ? eventFormErrors.clear('end') : null"
            >
              <span slot="icon">
                <i class="i-clock"></i>
              </span>
            </DatePicker>
            <small v-show="eventFormErrors.has('end')" class="help text-danger">
              {{ eventFormErrors.get('end') | translate }}
            </small>
          </div>
        </div>

        <div class="col-12">
          <b class="pl-2 text--bold" style="padding-top: -10px"
             v-bind:class="{'availableText': !isBusy, 'busyText': isBusy}">
            • {{ isBusy ? $t('leads.agenda.busy') : $t('leads.agenda.free') }}
          </b>
        </div>

      </div>

      <form class="evollu-form">
        <div class="row" v-if="facilities.length">
          <div class="col">
            <label class="form-label">{{ $t('agenda.appointments.facility') }}</label>
            <multiselect
              v-model="form.facility"
              :options="facilities"
              track-by="id"
              label="name"
              :custom-label="formatedEntityLabel"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="facilities.length < 2"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
          </div>
        </div>

        <div class="row" v-if="eventTypes.length">
          <div class="col">
            <label class="form-label">{{ $t('leads.agenda.event_type') }}</label>
            <multiselect
              v-model="form.type"
              :options="eventTypes"
              track-by="id"
              label="name"
              :custom-label="(val) => $t(`leads.agenda.event_types.${val.name}`)"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="eventTypes.length < 2"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-6">
            <va-input
              id="notes"
              :maxlength="32"
              v-model="form.notes"
              :label="$t('leads.agenda.notes')"
              :placeholder="$t('leads.agenda.notes')"
              @input="eventFormErrors.clear('notes')"
            />

            <small v-show="eventFormErrors.has('notes')" class="help text-danger">
              {{ $t(eventFormErrors.get('notes')) }}
            </small>
          </div>
        </div>

        <div v-if="lead">
          <br>
          <hr>
          <div class="row">
            <div class="col-12 col-md-6">
              <label class="form-label">{{ $t('leads.table.name') }}</label>
              <va-input
                :placeholder="$t('leads.table.name')"
                v-model="form.name"
                id="input-name"
              />
            </div>

            <div class="col-12 col-md-6">
              <label class="form-label">{{ $t('leads.details.address') }}</label>
              <va-input
                :placeholder="$t('leads.details.address')"
                v-model="form.address"
                id="input-address"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-md-6">
              <label class="form-label">{{ $t('leads.details.birthdate') }}</label>
              <DatePicker
                id="input-birthdate"
                v-model="form.birthdate"
                :config=datePickerBirthdateConfig
                :clearable="false"
                iconClass="i-clock"
                @input="eventFormErrors.has('birthdate') ? eventFormErrors.clear('birthdate') : null"
              >
              <span slot="icon">
                <i class="i-clock"></i>
              </span>
              </DatePicker>
              <small v-show="eventFormErrors.has('birthdate')" class="help text-danger">
                {{ eventFormErrors.get('birthdate') | translate }}
              </small>
            </div>

            <div class="col-12 col-md-6">
              <label class="form-label">{{ $t('leads.details.phone') }}</label>
              <vue-tel-input
                inputId="phone"
                style="width: inherit;"
                :key="vueTelInputRedrawIndex"
                v-model="form.phone"
                maxlength="15"
                :preferredCountries="countries.allowed"
                :disabledFetchingCountry="true"
                :defaultCountry="defaultCountrySelection"
                :placeholder="$t('people.inputs.phone')"
                @validate="mobilePhoneValidation">
              </vue-tel-input>
              <span class="small text-danger" v-show="form.phone !== '' && form.phone !== null && invalidMobilePhone">
          {{ $t('patients.errors.invalidPhone') }}
        </span>
            </div>
          </div>
        </div>
      </form>

    </va-modal>

    <ConfirmationModal
      v-if="showDeletingModal"
      :title="$t('leads.agenda.delete_event_title')"
      :body="$t('leads.agenda.delete_event_confirm')"
      @modal-confirmed="eventDeleteConfirm"
      @modal-canceled="eventDeleteCancel">
      <div slot="body" class="mt-4" v-if="this.lead">

        <va-input
          v-model="deleteComment"
          :label="$t('leads.agenda.delete_add_comment')"
          :placeholder="$t('leads.agenda.delete_add_comment')"
          :maxlength="255"
          type="textarea"
          :minRows="3"
          :maxRows="8"/>

      </div>
    </ConfirmationModal>

    <CalendarModalView
      v-if="showCalendarModal"
      :component="component"
      :service-slug="serviceSlug"
      :prefilledFormData="prefilledFormData"
      :prefilledFacility="prefilledFacility"
      @closedCalendarModal="handleCloseCalendarModal"
      @eventCreated="eventWasCreated"
    >

    </CalendarModalView>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import Errors from '@/utils/StandardErrors.js'
import MultiselectLabelFormatter from "@/mixins/MultiselectLabelFormatter";
import {LeadsAgendaService} from "@/api/LeadsService";
import DatePicker from "@/components/common/DatePicker";
import {Portuguese as PTLocale} from 'flatpickr/dist/l10n/pt'
import {Spanish as ESLocale} from 'flatpickr/dist/l10n/es'
import moment from "moment";
import {FORM_DEFAULTS} from "@/components/clinical/appointments/config";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import {mapState} from "vuex";
import {VueTelInput} from 'vue-tel-input'
import CalendarModalView from "@/components/leads-agenda/CalendarModalView";
import {flatten, uniqBy} from "lodash";

PTLocale.firstDayOfWeek = 1
const DEFAULT_EVENT_DURATION_MINTS = 45
const errorsPrefix = 'leads.agenda.events_errors'
const ActivityControloQualidadeId = 11

export default {
  name: "EventFormModal",
  components: {
    CalendarModalView,
    ConfirmationModal,
    DatePicker,
    Multiselect,
    VueTelInput
  },
  props: {
    fromEvent: {
      type: Object,
      required: false
    },
    prefilledFacility: {
      type: Object,
      required: false
    },
    prefilledUser: {
      type: Object,
      required: false
    },
    facilities: {
      type: Array,
      required: true
    },
    event: {
      type: Object,
      required: false
    },
    service: {
      type: Object,
      required: true
    },
    lead: {
      type: Object,
      required: false
    },
    showDatePickers: {
      type: Boolean,
      required: true
    },
    eventsSchedules: {
      type: Array,
      required: false
    }
  },
  mixins: [MultiselectLabelFormatter],
  data() {
    return {
      vueTelInputRedrawIndex: 0,
      showModal: false,
      showCalendarModal: false,
      showDeletingModal: false,
      isSubmitting: false,
      eventTypes: [],
      eventFormErrors: new Errors(),
      prefilledFormData: {},
      deleteComment: null,
      form: {
        start: null,
        end: null,
        type: null,
        user_id: null,
        user: null,
        facility_id: null,
        facility: null,
        entity_id: null,
        entity: null,
        name: null,
        phone: null,
        address: null,
        birthdate: null,
        notes: null
      },
      invalidMobilePhone: false,
      usersByFacilities: [],
    }
  },
  async created() {
    const eventTypesResult = await LeadsAgendaService.getEventTypes()
    if (this.lead) {
      this.eventTypes = eventTypesResult.data.data.filter(elem => elem.name === 'appointment')
    } else {
      this.eventTypes = eventTypesResult.data.data.filter(elem => elem.name !== 'appointment')
    }


    if (this.isUpdating) {
      this.fillForm()
    } else {
      this.prefillForm()
    }
  },
  mounted() {
    this.showModal = true

    // load users options
    if(this.isAdmin) {
      let facilitiesAdmin = this.entitiesAdmin
      facilitiesAdmin = facilitiesAdmin.filter(elem => elem.activity_id === ActivityControloQualidadeId && elem.status === 0)
      facilitiesAdmin = uniqBy(flatten(facilitiesAdmin.map(item => {
        return item.facilities
      })), 'id')

      LeadsAgendaService.getUsersOptions(facilitiesAdmin.map(facility => facility.id)).then(resp => {
        this.usersByFacilities = resp.data.data
      }).catch(err => {
        console.log(err)
      })
    }
  },
  methods: {
    fillForm: function () {
      this.form = JSON.parse(JSON.stringify(this.event))
      this.form.end = this.form.end.substring(0, 16)
      this.form.start = this.form.start.substring(0, 16)
      this.form.facility = this.facilities.find(facility => facility.id === this.event.facility.id)
      this.form.type = this.eventTypes.find(elem => elem.id === this.event.type.id)
      this.form.user_id = this.form.userId

    },
    prefillForm: function () {
      if (this.facilities.length === 1) {
        this.form.facility = this.facilities[0]
      }
      if (this.prefilledFacility) {
        this.form.facility = this.prefilledFacility
      }

      this.form.user = this.prefilledUser ? this.prefilledUser : this.$auth.user()


      if (!this.fromEvent) {
        let start = moment().add(FORM_DEFAULTS.NEXT_START_INCREMENT, FORM_DEFAULTS.NEXT_START_INTERVAL).startOf(FORM_DEFAULTS.NEXT_START_INTERVAL)
        let end = start.clone().add(FORM_DEFAULTS.START_INCREMENT, FORM_DEFAULTS.INCREMENT_INTERVAL)
        this.form.start = start.format('YYYY-MM-DD HH:mm')
        this.form.end = end.format('YYYY-MM-DD HH:mm')
      } else {
        this.form.start = this.fromEvent.start.substring(0, 16)
        this.form.end = this.fromEvent.end.substring(0, 16)
      }

      if (this.eventTypes.length === 1) {
        this.form.type = this.eventTypes[0]
      }

      if (this.lead) {

        this.form.name = this.lead.name
        if (this.lead.surname) {
          this.form.name = this.form.name + ' ' + this.lead.surname
        }

        if(this.lead.notes) {
          this.form.notes = this.lead.notes
        }

        if (this.lead.address_line_1) {
          this.form.address = this.lead.address_line_1
        }
        if (this.lead.address_line_2) {
          this.form.address = ' ' + this.lead.address_line_2
        }

        if (this.lead.address) {
          this.form.address = this.lead.address
        }

        if (this.lead.birthdate) {
          //if lead is an actual lead, parse the date this way
          if (this.lead.id) {
            var dateMomentObject = moment(this.lead.birthdate, "DD/MM/YYYY");
            this.form.birthdate = dateMomentObject.format('YYYY/MM/DD')
          }
            // if lead is not an actual lead but instead it is a partial form with info that was filled by
          // the user to create a new event, parse it from a different format
          else {
            var dateMomentObject = moment(this.lead.birthdate, "YYYY-MM-DD");
            this.form.birthdate = dateMomentObject.format('YYYY/MM/DD')
          }

        }

        if (this.lead.phone) {
          this.form.phone = this.lead.phone
        }

        if(this.lead.user) {
          this.form.user = this.lead.user
        }
      }

    },
    handleCloseCalendarModal() {
      this.showModal = true
      this.showCalendarModal = false
    },
    handleShowLeadDetail: function () {
      this.$emit('openLeadDetail', this.lead)
      this.showModal = false
    },
    mobilePhoneValidation: function (data) {
      this.invalidMobilePhone = !data.isValid
      if (data.isValid) {
        this.form.phone = data.number.international
      }
    },
    getModalTitle: function () {
      if (this.event) {
        return this.$t('leads.agenda.update_event')
      }
      return this.$t('leads.agenda.create_event')
    },
    getFormData() {
      let data = {
        start: this.$options.filters.utcTime(this.form.start),
        end: this.$options.filters.utcTime(this.form.end),
        type: this.form.type.id,
        user_id: this.form.user.id,
        facility_id: this.form.facility.id,
        entity_id: this.form.facility.entity_id,
        name: this.form.name,
        phone: this.form.phone ? this.form.phone.replace(/\s/g, '') : null,
        address: this.form.address,
        birthdate: this.form.birthdate,
        notes: this.form.notes,
        user: this.form.user
      }

      if (this.lead) {
        data.lead_id = this.lead.id ? this.lead.id : this.lead.lead_id
      }
      return data
    },
    submit: function () {
      let data = this.getFormData(this.isUpdating)
      this.isSubmitting = true

      if (this.isUpdating) {
        LeadsAgendaService.updateEvent(this.event.id, data)
          .then(response => {
            let event = response.data.data
            this.showToast(this.$t('leads.agenda.event_updated_success'), this.$store.state.successToastOptions)
            event.start = this.$options.filters.localTime(event.start)
            event.end = this.$options.filters.localTime(event.end)
            this.$emit('event-updated', {event})
          })
          .catch(errors => {
            if (errors.response.status === 422) {
              this.eventFormErrors.record(errors.response.data.errors, errorsPrefix)
              this.showToast(this.$t('clinical.appointments.errors.form_contains_errors'), this.$store.state.errorToastOptions)
            }
            console.log('errors', errors)
          })
          .finally(() => {
            this.isSubmitting = false
          })
      } else {
        this.prefilledFormData = data
        this.prefilledFacility = this.form.facility

        if (!this.showDatePickers) {
          setTimeout(() => {
            this.isSubmitting = false
            this.showModal = false
          }, 500)
          this.showCalendarModal = true
        } else {
          delete data.user
          LeadsAgendaService.createEvent(data)
            .then(response => {
              let event = response.data.data
              this.showToast(this.$t('leads.agenda.event_created'), this.$store.state.successToastOptions)
              this.$emit('event-created', {event})
            })
            .catch(errors => {
              console.log(errors)
              if (errors.response.status === 422) {
                this.eventFormErrors.record(errors.response.data.errors, errorsPrefix)
                this.showToast(this.$t('clinical.appointments.errors.form_contains_errors'), this.$store.state.errorToastOptions)
              }
              console.log('errors', errors)
            })
            .finally(() => {
              this.isSubmitting = false
            })

        }
      }
    },
    eventWasCreated(event) {
      this.showModal = false
      this.showCalendarModal = false
      setTimeout(() => {
        this.$emit('event-created', {event})
      }, 250)
    },
    cancel: function () {
      this.showModal = false
      setTimeout(() => {
        this.$emit('modal-closed')
      }, 250)
    },
    remove: function () {
      this.showModal = false
      this.showDeletingModal = true
    },

    eventDeleteConfirm: function () {
      this.isSubmitting = true
      LeadsAgendaService.deleteEvent(this.event.id, this.deleteComment)
        .then(response => {
          this.showToast(this.$t('leads.agenda.event_deleted_success'), this.$store.state.successToastOptions)
          this.$emit('event-deleted', {event: this.event})
        })
        .catch(error => {
          console.log(error)
          this.showToast(this.$t('clinical.appointments.actions.server_error'), this.$store.state.errorToastOptions)
        }).finally(() => {
        this.isSubmitting = false
      })
    },
    eventDeleteCancel: function () {
      this.eventFormErrors.clear()
      setTimeout(() => {
        this.showDeletingModal = false
        this.showModal = true
      }, 500)
    }
  },
  computed: {
    ...mapState({
      'countries': (state) => state.countries
    }),
    isBusy() {
      if (this.eventsSchedules && this.eventsSchedules.length > 0) {
        const start = moment(this.form.start)
        const end = moment(this.form.end)

        let schedules = this.eventsSchedules

        if (this.isUpdating) {
          const selfEventScheduleIdx = schedules.findIndex(sc => moment(sc.start).isSame(start) && moment(sc.end).isSame(end))
          schedules.splice(selfEventScheduleIdx, 1)
        }

        return this.eventsSchedules.some(elem => {
          const currEventStart = moment(elem.start)
          const currEventEnd = moment(elem.end)
          return ((start.isBetween(currEventStart, currEventEnd, undefined, '[)')
          || end.isBetween(currEventStart, currEventEnd, undefined, '(]'))
          || (currEventStart.isBetween(start, end, undefined, '[)')
          || currEventEnd.isBetween(start, end, undefined, '(]')))

          /*return (moment(this.form.start).isAfter(moment(elem.start)) && moment(this.form.start).isBefore(moment(elem.end))
            || moment(this.form.end).isAfter(moment(elem.start)) && moment(this.form.end).isBefore(moment(elem.end)))*/
        })
      }
      return false
    },
    serviceSlug() {
      return this.service.slug
    },
    component() {
      let component = this.service.components.filter(c => c.component === "leads-agenda")[0]
      component.service = this.service
      return component
    },
    isAdmin: function () {
      return this.component.service.user_permissions.some(permission => permission.info_type === 2200 && permission.access_type === 3)
    },
    entitiesAdmin: function () {
      return this.component.service.entities.filter(entity => this.eventsPermission.some(permission => (permission.entity_id === entity.id || permission.entity_id === null) && permission.access_type >= 3))
    },
    eventsPermission: function () {
      return this.component.service.user_permissions.filter(permission => permission.info_type === 2200)
    },
    authUserEntity: function () {
      return this.$auth.user().entity
    },
    defaultCountrySelection: function () {
      if (this.authUserEntity && this.authUserEntity.country) {
        return this.authUserEntity.country
      }
      return this.$store.state.countries.default
    },
    datePickerBirthdateConfig: function () {
      let config = {
        enableTime: false,
        dateFormat: 'Y-m-d',
        maxDate: moment().format('YYYY-MM-DD'),
        locale: PTLocale
      }

      return config
    },
    datePickerConfig: function () {
      let config = {
        enableTime: true,
        time_24hr: true,
        minuteIncrement: 15,
        dateFormat: 'Y-m-d H:i',
        defaultHour: '09'
      }

      if (this.$i18n.locale() === 'pt') {
        config.locale = PTLocale
      } else if (this.$i18n.locale() === 'es') {
        config.locale = ESLocale
      } else {
        config.locale = {firstDayOfWeek: 1}
      }
      return config
    },
    canSubmitForm: function () {
      return this.form.start && this.form.end && this.form.facility && this.form.user && this.form.type
    },
    isUpdating: function () {
      return this.event !== undefined && this.event !== null
    },
  },
  watch: {
    'form.type': function () {
      if (this.form.start && !this.isUpdating) {
        let increment = this.form.type && this.form.type.defaultTime ? this.form.type.defaultTime : DEFAULT_EVENT_DURATION_MINTS
        if (moment.duration(moment(this.form.end).diff(moment(this.form.start))).asMinutes() <= increment) {
          this.form.end = moment(start).add(increment, 'minutes').format('YYYY-MM-DD HH:mm')
        }
      }
    },
    'form.start': {
      handler: function (start) {
        if (start ) {
          let increment = this.form.type && this.form.type.defaultTime ? this.form.type.defaultTime : DEFAULT_EVENT_DURATION_MINTS
          if (moment.duration(moment(this.form.end).diff(moment(this.form.start))).asMinutes() <= increment) {
            this.form.end = moment(start).add(increment, 'minutes').format('YYYY-MM-DD HH:mm')
          }
        }
      }
    },
    'form.facility': {
      handler: function (facility) {
        if (facility && !this.prefilledUser) {
          if(this.usersByFacilities.length > 0) {
            const usersByFacility = this.usersByFacilities.filter(elem => elem.facility_id === facility.id)[0].users
            this.form.user = usersByFacility[usersByFacility.length - 1]
          }
        } else if (!this.prefilledUser) {
          this.$auth.user().id
        }
      },

    }
  }
}
</script>

<style lang="scss">
.action-icons {
  display: flex;
  flex-direction: row;
  position: absolute;
  right: 1.5rem;
  top: 1rem;
  z-index: 2;

  button {
    background-color: $evollu-gray-light !important;
    background-image: none !important;
  }

  i.va-button__content__icon {
    width: 1.25rem;
  }
}</style>
<style scoped>
.custom-outlined {
  border: 2px solid #dcdcdc;
  padding-top: 10px;
  padding-bottom: 20px;
  border-radius: 15px;
}

.availableText {
  color: #6cb412;
}

.busyText {
  color: #ad130b;
}

.datePickers {
  margin-bottom: 10px !important;
}
</style>
